// RelationshipTab.tsx
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Heart } from "lucide-react";
import AstriaUKV2Shell from "../AstriaUKV2Shell";
import { useUKV2Colors } from "../AstriaUKV2ThemeContext";
import {
  AstriaUKV2ResultCard,
  AstriaUKV2FieldBlock,
  UKV2LoadingState,
  UKV2ErrorState,
  UKV2ChatCta,
} from "../AstriaUKV2Card";
import {
  UKV2PersonFields,
  UKV2SubmitButton,
  PersonDetails,
  EMPTY_PERSON_DETAILS,
} from "../AstriaUKV2Wizard";
import { useAstriaUKV2Tab } from "../useAstriaUKV2Tab";
import { AstriaUKV2RelationshipData, Message } from "../../../../contexts/ChatContext";

const SUBCATEGORY_NAME = "Relationship";

const buildMessage = (self: PersonDetails, partner: PersonDetails) => `
Give me a Relationship reading — how I'm showing up with ${partner.name.trim() || "my partner"}.
My Date of Birth: ${self.dob.trim()}.
My Time of Birth: ${self.time.trim() || "Not provided"}.
My Place of Birth: ${self.place.trim() || "Not provided"}.
${partner.name.trim() || "My partner"}'s Date of Birth: ${partner.dob.trim()}.
${partner.name.trim() || "My partner"}'s Time of Birth: ${partner.time.trim() || "Not provided"}.
${partner.name.trim() || "My partner"}'s Place of Birth: ${partner.place.trim() || "Not provided"}.
`.trim();

// Colors must be read INSIDE AstriaUKV2Shell's subtree (see the note in
// ZodiacPersonalityTab.tsx) — these small components are rendered as
// `children`, not evaluated inline, so their hook calls resolve after the
// shell's theme provider has mounted.

const ResultView: React.FC<{
  data?: AstriaUKV2RelationshipData;
  message: Message | null;
  reset: () => void;
  goToChat: () => Promise<void>;
}> = ({ data, message, reset, goToChat }) => {
  const { t } = useTranslation();
  const { colors } = useUKV2Colors();

  if (data) {
    return (
      <>
        <AstriaUKV2ResultCard
          icon={<Heart className="w-5 h-5" />}
          title={t("ukv2.tabs.relationship.label")}
          subtitle={t("ukv2.relationship.subtitle")}
          shareText={`${data.opening}\n\n${data.strengths}\n\n${data.closing}`}
        >
          <AstriaUKV2FieldBlock label={t("ukv2.common.opening")} value={data.opening} />
          <AstriaUKV2FieldBlock label={t("ukv2.field.currentVibe")} value={data.current_vibe} />
          <AstriaUKV2FieldBlock label={t("ukv2.field.strengths")} value={data.strengths} />
          <AstriaUKV2FieldBlock label={t("ukv2.field.gentleAdjustment")} value={data.gentle_adjustment} />
          <AstriaUKV2FieldBlock label={t("ukv2.common.today")} value={data.today_action} />
          <AstriaUKV2FieldBlock label={t("ukv2.common.closing")} value={data.closing} />
        </AstriaUKV2ResultCard>
        <button
          onClick={reset}
          className="text-xs font-semibold transition-colors"
          style={{ marginTop: 14, color: colors.mutedColor }}
        >
          {t("ukv2.common.startNewReading")}
        </button>
        <UKV2ChatCta onClick={goToChat} />
      </>
    );
  }

  if (message) {
    return (
      <AstriaUKV2ResultCard icon={<Heart className="w-5 h-5" />} title={t("ukv2.tabs.relationship.label")}>
        <p
          style={{
            fontSize: 15,
            lineHeight: 1.75,
            color: colors.bodyColor,
            whiteSpace: "pre-wrap",
          }}
        >
          {message.content}
        </p>
      </AstriaUKV2ResultCard>
    );
  }

  return null;
};

const IntroForm: React.FC<{
  self: PersonDetails;
  setSelf: (v: PersonDetails) => void;
  partner: PersonDetails;
  setPartner: (v: PersonDetails) => void;
  onSubmit: () => void;
  formValid: boolean;
}> = ({ self, setSelf, partner, setPartner, onSubmit, formValid }) => {
  const { t } = useTranslation();
  const { colors } = useUKV2Colors();

  return (
    <>
      <div style={{ marginBottom: 20 }}>
        <h2
          style={{
            fontSize: 21,
            fontWeight: 700,
            color: colors.titleColor,
            marginBottom: 6,
          }}
        >
          {t("ukv2.tabs.relationship.label")}
        </h2>
        <p style={{ fontSize: 13.5, color: colors.mutedColor, lineHeight: 1.6 }}>
          {t("ukv2.relationship.intro")}
        </p>
      </div>

      <UKV2PersonFields
        heading={t("ukv2.common.yourDetails")}
        values={self}
        onChange={setSelf}
        namePlaceholder={t("ukv2.common.yourName")}
      />
      <UKV2PersonFields heading={t("ukv2.common.theirDetails")} values={partner} onChange={setPartner} />

      <UKV2SubmitButton onClick={onSubmit} disabled={!formValid} label={t("ukv2.relationship.submit")} />
    </>
  );
};

const RelationshipTab: React.FC = () => {
  const { t } = useTranslation();
  const { loading, error, message, send, reset, goToChat } = useAstriaUKV2Tab({
    subCategoryName: SUBCATEGORY_NAME,
  });

  const [self, setSelf] = useState<PersonDetails>(EMPTY_PERSON_DETAILS);
  const [partner, setPartner] = useState<PersonDetails>(EMPTY_PERSON_DETAILS);

  const started = !!message || loading || !!error;
  const formValid = !!self.dob.trim() && !!partner.dob.trim();

  const handleSubmit = () => send(buildMessage(self, partner));

  const data = message?.astriaUKV2Data as AstriaUKV2RelationshipData | undefined;

  if (started) {
    return (
      <AstriaUKV2Shell active="relationship">
        {loading && <UKV2LoadingState label={t("ukv2.loading.relationship")} />}
        {!loading && error && <UKV2ErrorState message={error} onRetry={handleSubmit} />}
        {!loading && !error && (
          <ResultView data={data} message={message} reset={reset} goToChat={goToChat} />
        )}
      </AstriaUKV2Shell>
    );
  }

  return (
    <AstriaUKV2Shell active="relationship">
      <IntroForm
        self={self}
        setSelf={setSelf}
        partner={partner}
        setPartner={setPartner}
        onSubmit={handleSubmit}
        formValid={formValid}
      />
    </AstriaUKV2Shell>
  );
};

export default RelationshipTab;
